"use client";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  Avatar,
  IconButton,
  Dialog,
  DialogActions,
  Button,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/Close";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import {
  getPrivacySwitchesAPI,
  updatePrivacySwitchesAPI,
  getBlockedUsersAPI,
  unblockUserAPI,
} from "../../../../api/userApi";
import { usePrivacySettingsStyles } from "./styles";
import { getUserType } from "../../../../utils/getUserType";
import { useAppSelector } from "../../../../redux/typedHooks";
import { UserInterface } from "../../../../types/user";

interface CreatorPrivacySwitches {
  allowTipsWithoutSubscription: boolean;
  allowCommentsOnPosts: boolean;
  hideRealName: boolean;
  showOnlineStatus: boolean;
  allowMessagesWithoutSubscription: boolean;
  blurInboxPhotos: boolean;
}

/** Populated user or raw ObjectId string when populate is missing */
type BlockedIdValue =
  | string
  | {
    _id?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
  }
  | null
  | undefined;

interface BlockedUserRecord {
  _id?: string;
  blockedId?: BlockedIdValue;
}

const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

const getTargetBlockedUserId = (item: BlockedUserRecord): string | null => {
  const b = item.blockedId;
  if (!b) return null;
  if (typeof b === "string" && OBJECT_ID_RE.test(b)) return b;
  if (typeof b === "object" && b._id && OBJECT_ID_RE.test(String(b._id))) {
    return String(b._id);
  }
  return null;
};

const getBlockedDisplayName = (item: BlockedUserRecord): string => {
  const b = item.blockedId;
  if (!b) return "Невідомий користувач";
  if (typeof b === "string") {
    return OBJECT_ID_RE.test(b) ? "Невідомий користувач" : b;
  }
  const fn = (b.firstName || "").trim();
  const ln = (b.lastName || "").trim();
  const full = `${fn} ${ln}`.trim();
  if (full) return full;
  if (b.username) return b.username;
  return "Невідомий користувач";
};

const getBlockedAvatarSrc = (item: BlockedUserRecord): string => {
  const b = item.blockedId;
  if (b && typeof b === "object" && b.profileImg) return b.profileImg;
  return "";
};

const normalizeBlockListResponse = (res: unknown): BlockedUserRecord[] => {
  if (!res || typeof res !== "object") return [];
  const r = res as {
    success?: boolean;
    data?: unknown;
  };
  if (r.success === true && Array.isArray(r.data)) return r.data as BlockedUserRecord[];
  if (Array.isArray(r.data)) return r.data as BlockedUserRecord[];
  if (Array.isArray(res)) return res as BlockedUserRecord[];
  return [];
};

const getCachedPrivacySwitches = (): CreatorPrivacySwitches => {
  const cached = localStorage.getItem("creatorPrivacySwitches");
  if (cached) {
    return JSON.parse(cached) as CreatorPrivacySwitches;
  }
  return {
    allowTipsWithoutSubscription: false,
    allowCommentsOnPosts: true,
    hideRealName: false,
    showOnlineStatus: true,
    allowMessagesWithoutSubscription: false,
    blurInboxPhotos: true,
  };
};

const PrivacySettings = () => {
  const classes = usePrivacySettingsStyles();
  const [privacySwitches, setPrivacySwitches] = useState<CreatorPrivacySwitches>(
    getCachedPrivacySwitches()
  );
  const [blockedUsers, setBlockedUsers] = useState<BlockedUserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingField, setUpdatingField] = useState<
    keyof CreatorPrivacySwitches | null
  >(null);
  const [unblockingId, setUnblockingId] = useState<string | null>(null);
  const [unblockDialogItem, setUnblockDialogItem] =
    useState<BlockedUserRecord | null>(null);
  const [, setError] = useState<string | null>(null);

  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const userType = getUserType(user);
  const isViewer = userType?.title?.toLowerCase() === "viewer";


  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const [privacySwitchesResponse, blockedUsersResponse] =
          await Promise.all([
            getPrivacySwitchesAPI(),
            getBlockedUsersAPI(),
          ]);

        if (privacySwitchesResponse.success) {
          const normalized: CreatorPrivacySwitches = {
            allowTipsWithoutSubscription:
              privacySwitchesResponse?.data?.allowTipsWithoutSubscription ===
              true,
            allowCommentsOnPosts:
              privacySwitchesResponse?.data?.allowCommentsOnPosts !== false,
            hideRealName: privacySwitchesResponse?.data?.hideRealName === true,
            showOnlineStatus:
              privacySwitchesResponse?.data?.showOnlineStatus !== false,
            allowMessagesWithoutSubscription:
              privacySwitchesResponse?.data?.allowMessagesWithoutSubscription ===
              true,
            blurInboxPhotos:
              privacySwitchesResponse?.data?.blurInboxPhotos !== false,
          };
          setPrivacySwitches(normalized);
          localStorage.setItem(
            "creatorPrivacySwitches",
            JSON.stringify(normalized)
          );
        } else {
          setError(
            privacySwitchesResponse.message || "Failed to fetch privacy switches"
          );
        }

        setBlockedUsers(normalizeBlockListResponse(blockedUsersResponse));
      } catch (err) {
        setError("Something went wrong while fetching settings");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handlePrivacyChange = async (
    setting: keyof CreatorPrivacySwitches,
    value: boolean
  ) => {
    setError(null);
    const previous = privacySwitches[setting];
    const updatedSettings = {
      ...privacySwitches,
      [setting]: value,
    };
    setPrivacySwitches(updatedSettings);
    setUpdatingField(setting);
    try {
      const response = await updatePrivacySwitchesAPI({ [setting]: value });
      if (response.success) {
        localStorage.setItem(
          "creatorPrivacySwitches",
          JSON.stringify(updatedSettings)
        );
      } else {
        setPrivacySwitches(prev => ({ ...prev, [setting]: previous }));
        setError(response.message || "Failed to update privacy switch");
        toast.error(response.message || "Failed to update privacy switch");
      }
    } catch (err) {
      setPrivacySwitches(prev => ({ ...prev, [setting]: previous }));
      setError("Something went wrong while updating privacy switch");
      toast.error("Something went wrong while updating privacy switch");
      console.error(err);
    } finally {
      setUpdatingField(null);
    }
  };

  /** Returns true if unblock succeeded (caller may close dialog). */
  const performUnblock = async (item: BlockedUserRecord): Promise<boolean> => {
    const targetUserId = getTargetBlockedUserId(item);
    if (!targetUserId) {
      toast.error("Немає ідентифікатора користувача для розблокування");
      return false;
    }

    const listSnapshot = blockedUsers;

    setBlockedUsers(prev =>
      item._id
        ? prev.filter(e => e._id !== item._id)
        : prev.filter(e => getTargetBlockedUserId(e) !== targetUserId)
    );
    setUnblockingId(targetUserId);

    try {
      const response = await unblockUserAPI(targetUserId);
      if (response?.success) {
        toast.success(response.message || "User unblocked");
        return true;
      }
      setBlockedUsers(listSnapshot);
      toast.error(response?.message || "Failed to unblock user");
      return false;
    } catch (err: unknown) {
      setBlockedUsers(listSnapshot);
      const data = err as { error?: string; message?: string };
      const msg =
        data?.error ||
        data?.message ||
        "Could not remove user from block list";
      toast.error(msg);
      return false;
    } finally {
      setUnblockingId(null);
    }
  };

  const closeUnblockDialog = () => {
    if (unblockingId) return;
    setUnblockDialogItem(null);
  };

  const renderSettingRow = (
    label: string,
    checked: boolean,
    onChange: (value: boolean) => void
  ) => (
    <Box className={classes.settingCard}>
      <Typography className={classes.settingLabel}>{label}</Typography>
      <Switch
        checked={checked}
        onChange={event => onChange(event.target.checked)}
        disabled={updatingField !== null}
        color="default"
        classes={{
          root: classes.switchRoot,
          switchBase: classes.switchBase,
          thumb: classes.switchThumb,
          track: classes.switchTrack,
          checked: classes.switchChecked,
        }}
      />
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>
        Profile Privacy
      </Typography>
      <Typography className={classes.passwordText}>
        Control who can see and interact with your profile
      </Typography>
      <Typography className={classes.groupTitle}>Profile view</Typography>
      <Box className={classes.groupWrapper}>
        {!isViewer && renderSettingRow(
          "Allow tipping without subscription",
          privacySwitches.allowTipsWithoutSubscription,
          checked => handlePrivacyChange("allowTipsWithoutSubscription", checked)
        )}
        {!isViewer && renderSettingRow(
          "Allow comments on posts",
          privacySwitches.allowCommentsOnPosts,
          checked => handlePrivacyChange("allowCommentsOnPosts", checked)
        )}
        {renderSettingRow(
          "Do not show my real name",
          privacySwitches.hideRealName,
          checked => handlePrivacyChange("hideRealName", checked)
        )}
        {renderSettingRow(
          "Let others see when I'm online",
          privacySwitches.showOnlineStatus,
          checked => handlePrivacyChange("showOnlineStatus", checked)
        )}
      </Box>

      <Typography className={classes.groupTitle}>Messages</Typography>
      <Box className={classes.groupWrapper}>
        {!isViewer && renderSettingRow(
          "Allow messages without subscription",
          privacySwitches.allowMessagesWithoutSubscription,
          checked =>
            handlePrivacyChange("allowMessagesWithoutSubscription", checked)
        )}
        {renderSettingRow(
          "Blur inbox photos",
          privacySwitches.blurInboxPhotos,
          checked => handlePrivacyChange("blurInboxPhotos", checked)
        )}
      </Box>

      <Typography className={classes.groupTitle}>Black list</Typography>
      {blockedUsers.length === 0 ? (
        <Typography className={classes.blacklistEmpty}>
          No users on your black list
        </Typography>
      ) : (
        <Box className={classes.blacklistList}>
          {blockedUsers.map(item => {
            const displayName = getBlockedDisplayName(item);
            const avatarSrc = getBlockedAvatarSrc(item);
            const targetId = getTargetBlockedUserId(item);
            const rowKey = item._id || targetId || displayName;
            const busy = targetId != null && unblockingId === targetId;

            return (
              <Box key={rowKey} className={classes.blacklistRow}>
                <Box className={classes.blacklistUser}>
                  <Avatar
                    src={avatarSrc}
                    alt={displayName}
                    className={classes.blacklistAvatar}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography className={classes.blacklistName}>
                    {displayName}
                  </Typography>
                </Box>
                <Box className={classes.blacklistAction}>
                  <IconButton
                    aria-label="Unblock user"
                    disabled={!targetId || busy}
                    onClick={() => setUnblockDialogItem(item)}
                    size="small"
                    className={classes.blacklistIconButton}
                  >
                    <DeleteOutlineIcon
                      className={classes.blacklistActionIcon}
                    />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog
        open={Boolean(unblockDialogItem)}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            closeUnblockDialog();
          }
        }}
        classes={{ paper: classes.unblockDialogPaper }}
        maxWidth={false}
        fullWidth
      >
        {unblockDialogItem ? (
          <>
            <Box className={classes.unblockDialogHeader}>
              <Typography className={classes.unblockDialogTitle} component="div">
                Are you sure you want to remove this person from your Black list?
              </Typography>
              <IconButton
                aria-label="Close"
                className={classes.unblockDialogClose}
                onClick={closeUnblockDialog}
                disabled={Boolean(unblockingId)}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography className={classes.unblockDialogBody} component="p">
              If you unblock this user, they will once again be able to view your
              profile and send you messages. If this user makes you feel unsafe or
              violates our safety rules, you can always report them and block them
              again at any time.
            </Typography>
            <Box className={classes.unblockDialogUserCard}>
              <Avatar
                src={getBlockedAvatarSrc(unblockDialogItem)}
                alt={getBlockedDisplayName(unblockDialogItem)}
                className={classes.unblockDialogAvatar}
              >
                {getBlockedDisplayName(unblockDialogItem)
                  .charAt(0)
                  .toUpperCase()}
              </Avatar>
              <Typography className={classes.unblockDialogUserName}>
                {getBlockedDisplayName(unblockDialogItem)}
              </Typography>
            </Box>
            <DialogActions className={classes.unblockDialogActions}>
              <Button
                className={classes.unblockDialogCancel}
                onClick={closeUnblockDialog}
                disabled={Boolean(unblockingId)}
              >
                Cancel
              </Button>
              <Button
                className={classes.unblockDialogConfirm}
                onClick={async () => {
                  const ok = await performUnblock(unblockDialogItem);
                  if (ok) setUnblockDialogItem(null);
                }}
                disabled={
                  Boolean(unblockingId) ||
                  !getTargetBlockedUserId(unblockDialogItem)
                }
              >
                Unblock User
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </div>
  );
};

export default PrivacySettings;
